import { useCallback, useMemo, useState } from "react";

//order matters - validators take precedence from top to bottom
const inputValidators = {
    required: () => {
        return buildValidator(notEmpty, 'required');
    },
    numeric: () => {
        return buildValidator(isNumeric(), 'numeric');
    },
    min: (value) => {
        return buildValidator(min(value), 'min');
    },
    max: (value) => {
        return buildValidator(max(value), 'max');
    },
    date: () => {
        return buildValidator(isValidDate, 'invalidDate');
    },
    disablePast: () => {
        return buildValidator(isAfter(new Date(new Date().setDate(new Date().getDate() - 1))), 'disablePast');
    },
    disableFuture: () => {
        return null;
    },
    minDate: (minDate) => {
        return buildValidator(isAfter(minDate), 'minDate');
    },
    maxDate: (maxDate) => {
        return buildValidator(isBefore(maxDate), 'maxDate');
    },
    shouldDisableDate: (shouldDisableDateFunction) => {
        return buildValidator((value) => !shouldDisableDateFunction(value), 'dateDisabled');
    },
    unique: ({ existingValues }) => {
        return buildValidator(isUnique(existingValues), 'unique');
    },
    email: () => {
        return buildValidator(isEmail(), 'email');
    },
    phoneNumber: () => {
        return buildValidator(isPhoneNumber(), 'phone number');
    }
}

const buildValidator = (validateFunction, name) => {
    return {
        validate: (value) => {
            const valid = validateFunction(value);
            return {
                valid,
                reason: valid ? null : name,
            }
        }
    }
}

// general validators

const notEmpty = value => !(value === null || value === undefined || value.length === 0);

const isUnique = (existingValues) => {
    if (!existingValues || !Array.isArray(existingValues)) {
        return null;
    }

    return (value) => {
        return !existingValues.includes(value);
    }
}

// number validators

const min = (minValue) => {
    return (value) => !isNaN(value) && value >= minValue;
}

const max = (maxValue) => {
    return (value) => !isNaN(value) && value <= maxValue;
}

// date validators

const isValidDate = value => value instanceof Date && !isNaN(value.valueOf());

const isAfter = (minDate) => {
    if (!(minDate instanceof Date)) {
        return null;
    }

    return (date) => {
        if (!date) {
            return true;
        }
        if (!(date instanceof Date)) {
            return false;
        }

        return date >= minDate;
    }
}

const isBefore = (maxDate) => {
    if (!(maxDate instanceof Date)) {
        return null;
    }

    return (date) => {
        if (!date) {
            return true;
        }
        if (!(date instanceof Date)) {
            return false;
        }

        return date <= maxDate;
    }
}

const isEmail = () => {
    return (value) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(value);
    }
};

const isPhoneNumber = () => {
    return (value) => {
        const phoneRegex = /^\d+$/;
        return phoneRegex.test(value);
    }
}

const isNumeric = () => {
    return (value) => {
        const numericRegex = /^-?(0|[1-9]\d*)+(\.\d{1,2})?$/;
        //const numericRegex = /^\d+(\.\d+)?$/;
        return numericRegex.test(value);
    }
}

/**
 * Supported validators are: required, min, max, date, disablePast, disableFuture, minDate, maxDate, shouldDisableDate, unique
 * @param {*} props 
 * @returns 
 */
const extractInputValidatorsFromProps = (props) => {
    const validatorsFromProps = Object.entries(inputValidators).filter(([key, value]) => props[key] !== undefined).map(([key, value]) => [key, props[key]]);

    const prepareValidator = ([key, value]) => {
        return (/*0 must be true*/value !== undefined && value !== false && value !== null) && inputValidators[key](value);
    }

    return validatorsFromProps.map(prepareValidator).filter(validator => validator && validator.validate);
}

/**
 * 
 * @param {*} validators extracted with extractInputValidatorsFromProps function
 * @param {*} getErrorMessage 
 * @returns [validationResult: Bool, validate: Func] validation result and validate function to be called whenever re-validation is needed
 */
export const useInputValidators = (validators, getErrorMessage) => {
    const defaultValidationResult = useMemo(() => ({
        valid: true,
        reason: null,
        message: null,
    }), []);

    const [validationResult, setValidationResult] = useState(defaultValidationResult);

    const validate = useCallback((value) => {
        let newValidationResult = {
            ...validationResult,
        }

        if (validators.length === 0) {
            newValidationResult = defaultValidationResult;
        }
        else {
            for (const validator of validators) {
                newValidationResult = validator.validate(value);

                if (!newValidationResult.valid) {
                    break;
                }
            }
        }

        if (!newValidationResult.valid && getErrorMessage) {
            newValidationResult.message = getErrorMessage(newValidationResult.reason);
        }

        if (validationResult.valid !== newValidationResult.valid || validationResult.reason !== newValidationResult.reason) {
            setValidationResult(newValidationResult);
        }

        return newValidationResult;
    }, [validationResult, setValidationResult, validators, defaultValidationResult, getErrorMessage])

    return [validationResult, validate];
}

export const validationUtils = {
    extractInputValidatorsFromProps: extractInputValidatorsFromProps,
}